<script setup lang="ts">
import { ArticleProps } from "./Article.props"
import slugify from "@sindresorhus/slugify"
import { mapProducts } from "~/utils/mapProducts"
import { parse } from "node-html-parser"
import { Heading } from "./Utils/TitleIndex.props"
import { Remarkable } from "remarkable"
import { WithContext, Article } from "schema-dts"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { productUtils } from "~/utils/product"

const baseUrl = useRuntimeConfig().public.BASE_URL_APP
const indexProducts = useAlgoliaInitIndex(`TOP_PRODUCTS${checkEnvSite()}`)
const route = useRoute()

const findItemsFromAlgolia = async (productCodes: string[]) => {
  const response = await indexProducts.getObjects(
    productCodes.map((code) => `product_${code}`)
  )

  const products = response?.results ?? []
  const itemsInStock =
    await productUtils.getItemsInStockOrRelatedItemsFromAlgolia(products)

  return mapProductsAlgolia(itemsInStock as ProductAlgolia[]).filter(
    (item) => item as ProductAlgolia
  )
}
const props = defineProps<ArticleProps>()

const headings = ref<Heading[]>([])

const mdText = ref<string>("")
const { data: productBlocksSuggestion } = await useAsyncData(
  "findItemsFromAlgolia",
  async () =>
    props.suggestedProducts &&
    (await findItemsFromAlgolia(props.suggestedProducts)),
  { immediate: true, server: true, lazy: false }
)

const remarkable = new Remarkable({ html: true })

// Elaborate text as concatenation of content components or (as a fallback) as the main content in the props

const text =
  props.contents && props.contents.length > 0
    ? props.contents
        .filter((content) => !!content.props?.content)
        .map((content) => content.props.content)
        ?.join("\n")
    : props.text

const root = parse(remarkable.render(text))
// pick just h2 and h3 because others headings were not styled by design
for (const h of root.querySelectorAll("h2,h3")) {
  const slug = h.getAttribute("id") || slugify(h.textContent)
  h.setAttribute("id", slug)
  h.insertAdjacentHTML(
    "afterbegin",
    `<a class="header-anchor" href="#${slug}"></a> `
  )

  headings.value.push({
    id: h.id ?? "",
    text: h.textContent ?? "",
    tagName: h.tagName.toLowerCase()
  })
}

mdText.value = root?.toString() ?? ""

const wordCounter = text
  ? useWordCounter(text, { isHtml: true })
  : {
      wordsCount: 0
    }

const jsonLd: ComputedRef<WithContext<Article>> = computed(() => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: props.title,
  datePublished: props.firstPublishingDate,
  dateModified: props.lastPublishingDate,
  ...(props.author
    ? {
        author: {
          "@type": "Person",
          name: props.author ?? ""
        }
      }
    : {}),
  publisher: {
    "@type": "Organization",
    url: "https://www.topfarmacia.it/",
    name: "Top Farmacia",
    logo: {
      "@type": "ImageObject",
      url: `${baseUrl}/images/LogoTopFarmaciaOnline.svg`
    }
  },
  articleSection: props.category.name,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": route.fullPath
  },
  wordCount: wordCounter.wordsCount
}))

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(jsonLd.value))
    }
  ]
})
</script>

<template>
  <div class="wrapper-wiki-article">
    <div class="grid-standard">
      <UtilsBreadcrumbs
        v-bind="breadcrumbs"
        class="negative-margined col-span-full"
      />
      <div class="col-span-full mt-4 md:col-span-6 md:col-start-2 md:mt-6">
        <h1
          class="
            mammoth-bold
            before:bg-tiffany-30
            relative
            inline-block
            before:absolute
            before:-bottom-0
            before:left-0
            before:z-[-1]
            before:block
            before:h-4
            before:w-[120%]
          "
        >
          {{ title }}
        </h1>
      </div>

      <OrganismsWikiUtilsTitleIndex
        v-if="headings?.length"
        class="col-span-full mt-4 md:col-span-4 md:col-start-8 md:row-start-3"
        :headings="headings"
      />

      <div
        class="
          wiki-article-text
          col-span-full
          md:col-span-5 md:col-start-2
          md:row-span-3
          md:-mt-4
        "
        v-if="contents && contents.length > 0"
      >
        <DynamicContent
          class="cms-content"
          style="overflow: hidden"
          :components="contents"
        />
      </div>

      <div
        id="textWrapper"
        class="
          wiki-article-text
          col-span-full
          md:col-span-5 md:col-start-2
          md:row-span-3
          md:-mt-4
        "
        v-else
        v-html="mdText"
      />
      <div
        class="
          col-span-full
          flex flex-col
          pb-4
          md:col-start-2 md:col-end-12
          md:flex-row
          md:items-center
          md:justify-between
          md:pb-0
        "
      >
        <div class="flex items-center gap-2 py-4 md:py-8">
          <span class="beaver">{{ $t("wiki.categories.category") }}:</span>
          <BadgeTag
            v-if="category.name"
            class="beaver-medium"
            :text="category.name"
            :url="'../' + category.slug"
          />
        </div>

        <UtilsShare>
          <template #default>
            <UtilsButton
              :text="$t('wiki.categories.share')"
              theme="link-green"
              class="custom-btn-no-padding custom-btn-start"
            >
              <template #icon>
                <UtilsIcon
                  name="Share.svg"
                  color="green-main"
                  class="h-5 w-5"
                />
              </template>
            </UtilsButton>
          </template>
        </UtilsShare>
      </div>

      <ProductBlocksSuggestion
        v-if="productBlocksSuggestion"
        :products="productBlocksSuggestion"
        class="
          col-span-full
          -mx-4
          md:col-span-4 md:col-start-8
          md:row-start-4
          md:mx-0
          md:mt-6
        "
      />
    </div>
    <CartUtilsLogin showLoginButton />

    <OrganismsWikiUtilsPopular
      v-if="popularArticles?.length === 3"
      :title="$t('wiki.categories.pupularArticles')"
      :category="category.name"
      :path="category.slug"
      bgColor="grey"
      isTitleCenter
      isOffetGrid
      :articles="popularArticles"
    />

    <BannerNewsletter class="padded" />
  </div>
</template>
